/**
 * ScrollManager - Handles scroll behavior across the site
 * 
 * Default: Scroll to top on navigation
 * Exception: Restore scroll position when returning from product detail page
 *            to search, category, or subcategory pages
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Storage key for scroll positions
const SCROLL_STORAGE_KEY = 'cgc_scroll_positions';

// Pages that support scroll restoration
const RESTORABLE_PAGES = ['/search', '/womens-fashion', '/beauty', '/lifestyle'];

/**
 * Check if current path is a product detail page
 */
const isProductDetailPage = (pathname) => {
  // Product detail pages have format: /{category}/{subcategory}/{product-slug}
  // or could be direct product URLs
  const segments = pathname.split('/').filter(Boolean);
  return segments.length >= 3;
};

/**
 * Check if current path is a list page (search, category, or subcategory)
 */
const isListPage = (pathname) => {
  if (pathname === '/search') return true;
  const segments = pathname.split('/').filter(Boolean);
  // Category page: /category-slug (1 segment)
  // Subcategory page: /category-slug/subcategory-slug (2 segments)
  return segments.length <= 2 && segments.length > 0;
};

/**
 * Get stored scroll positions
 */
const getStoredPositions = () => {
  try {
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save scroll position for a path
 */
export const saveScrollPosition = (path) => {
  try {
    const positions = getStoredPositions();
    positions[path] = window.scrollY;
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch (e) {
    console.warn('Failed to save scroll position:', e);
  }
};

/**
 * Get saved scroll position for a path
 */
const getSavedScrollPosition = (path) => {
  const positions = getStoredPositions();
  return positions[path] || 0;
};

/**
 * Clear scroll position for a path
 */
const clearScrollPosition = (path) => {
  try {
    const positions = getStoredPositions();
    delete positions[path];
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch (e) {
    console.warn('Failed to clear scroll position:', e);
  }
};

/**
 * Store the previous path to detect back navigation from product pages
 */
const PREV_PATH_KEY = 'cgc_prev_path';

export const setPreviousPath = (path) => {
  try {
    sessionStorage.setItem(PREV_PATH_KEY, path);
  } catch (e) {
    console.warn('Failed to save previous path:', e);
  }
};

const getPreviousPath = () => {
  try {
    return sessionStorage.getItem(PREV_PATH_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * ScrollManager Component
 * Add this to App.js to manage scroll behavior globally
 */
const ScrollManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = getPreviousPath();
    
    // Check if we're returning to a list page from a product detail page
    const isReturningFromProduct = isProductDetailPage(previousPath) && isListPage(currentPath);
    const isBackNavigation = navigationType === 'POP';
    
    if (isReturningFromProduct && isBackNavigation) {
      // Restore scroll position
      const savedPosition = getSavedScrollPosition(currentPath);
      if (savedPosition > 0) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo(0, savedPosition);
        }, 100);
        // Clear after restoring (one-time use)
        clearScrollPosition(currentPath);
      }
    } else if (!isBackNavigation || !isListPage(currentPath)) {
      // Default: scroll to top for new navigations
      window.scrollTo(0, 0);
    }
    
    // Update previous path for next navigation
    setPreviousPath(currentPath);
    prevPathRef.current = currentPath;
  }, [location.pathname, navigationType]);

  return null;
};

export default ScrollManager;
